import React, { useCallback, useEffect, useState } from "react";
import { useSnackbarController } from "@rebasepro/app";
import { Button, Checkbox, Chip, cls, defaultBorderMixin, Card, TextField, Typography } from "@rebasepro/ui";
import { client } from "../lib/rebaseClient";
import { describeError, startOfTomorrow } from "../inbox/shared";

/**
 * What is still to do for a person or a company.
 *
 * The agent can raise a task; only a person can finish one. Due dates are
 * picked from four buttons rather than a calendar, because "tomorrow" is what
 * people mean nine times in ten and a date field is one more thing to click.
 */

type Task = {
    id: string;
    title?: string | null;
    status?: "pending" | "done" | "dropped" | null;
    priority?: "high" | "normal" | "low" | null;
    due_date?: string | null;
    source?: string | null;
    completed_at?: string | null;
};

const DUE: { label: string; days: number }[] = [
    { label: "Today", days: 0 },
    { label: "Tomorrow", days: 1 },
    { label: "In 3 days", days: 3 },
    { label: "Next week", days: 7 }
];

function dueOn(days: number): string {
    const when = new Date();
    when.setDate(when.getDate() + days);
    when.setHours(9, 0, 0, 0);
    return when.toISOString();
}

function dueLabel(task: Task): { text: string; overdue: boolean } | null {
    if (!task.due_date) return null;
    const due = new Date(task.due_date);
    const overdue = due.getTime() < new Date(new Date().setHours(0, 0, 0, 0)).getTime();
    const today = due.getTime() < startOfTomorrow().getTime() && !overdue;
    return {
        text: overdue ? `overdue since ${due.toLocaleDateString([], { day: "numeric", month: "short" })}` : today ? "today" : due.toLocaleDateString([], { weekday: "short", day: "numeric", month: "short" }),
        overdue
    };
}

export function TasksPanel({ prospectId, companyId }: { prospectId?: string | null; companyId?: string | null }) {
    const snackbar = useSnackbarController();
    const [tasks, setTasks] = useState<Task[] | null>(null);
    const [title, setTitle] = useState("");
    const [days, setDays] = useState(1);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | undefined>();

    const load = useCallback(async () => {
        const owner = prospectId ? { prospectId: ["==", prospectId] } : companyId ? { companyId: ["==", companyId] } : null;
        if (!owner) { setTasks([]); return; }
        try {
            const { data } = await client.data.collection<Task>("tasks").find({
                where: { ...owner, status: ["==", "pending"] } as never,
                orderBy: ["due_date", "asc"],
                limit: 50
            });
            setTasks(data ?? []);
        } catch (err) {
            setError(describeError(err));
            setTasks([]);
        }
    }, [prospectId, companyId]);

    useEffect(() => { void load(); }, [load]);

    const add = async () => {
        if (!title.trim()) return;
        setSaving(true);
        try {
            await client.data.collection("tasks").create({
                title: title.trim().slice(0, 300),
                prospect: prospectId ?? null,
                company: companyId ?? null,
                status: "pending",
                priority: "normal",
                source: "human",
                due_date: dueOn(days)
            });
            setTitle("");
            await load();
        } catch (err) {
            setError(describeError(err));
        } finally {
            setSaving(false);
        }
    };

    const finish = async (task: Task, status: "done" | "dropped") => {
        setTasks(list => list?.filter(t => t.id !== task.id) ?? list);
        try {
            await client.data.collection("tasks").update(task.id, { status, completed_at: new Date().toISOString() });
            snackbar.open({ type: "success", message: status === "done" ? `Done: ${task.title}` : `Dropped: ${task.title}` });
        } catch (err) {
            setError(describeError(err));
            await load();
        }
    };

    return (
        <div className="flex flex-col gap-3">
            <Typography variant="subtitle2">To do{tasks?.length ? ` (${tasks.length})` : ""}</Typography>

            {(tasks ?? []).map(task => {
                const due = dueLabel(task);
                return (
                    <div key={task.id} className={cls("flex items-center gap-2 px-3 py-2 rounded-lg border", defaultBorderMixin)}>
                        <Checkbox size="small" checked={false} onCheckedChange={() => void finish(task, "done")} />
                        <Typography variant="body2" className="grow min-w-0 break-words">{task.title}</Typography>
                        {task.priority === "high" && <Chip size="small" colorScheme="redLighter">high</Chip>}
                        {due && <Chip size="small" colorScheme={due.overdue ? "redLighter" : "grayLighter"}>{due.text}</Chip>}
                        <Button size="small" variant="text" onClick={() => void finish(task, "dropped")}>drop</Button>
                    </div>
                );
            })}
            {tasks?.length === 0 && <Typography variant="body2" color="secondary">Nothing to do.</Typography>}

            <Card className={cls("p-3 flex flex-col gap-2", defaultBorderMixin)}>
                <TextField
                    size="small"
                    label="Add a task"
                    placeholder="Send them the RLS docs, follow up about the trial…"
                    value={title}
                    onChange={event => setTitle(event.target.value)}
                    onKeyDown={event => { if (event.key === "Enter") { event.preventDefault(); void add(); } }}
                />
                <div className="flex gap-1.5 items-center flex-wrap">
                    {DUE.map(option => (
                        <Button key={option.days} size="small" variant={days === option.days ? "filled" : "text"} onClick={() => setDays(option.days)}>
                            {option.label}
                        </Button>
                    ))}
                    <Button size="small" variant="outlined" className="ml-auto" disabled={saving || !title.trim()} onClick={() => void add()}>Add</Button>
                </div>
            </Card>
            {error && <Typography variant="caption" color="error">{error}</Typography>}
        </div>
    );
}
